import { put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { AnyAction } from "typescript-fsa";

import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DocumentServiceFactory, DocumentModel, Document } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { DocumentService, EntityInstancePath, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";

import { createZeroIndexedEntityInstancePath } from "../../utilities/pathHelpers";
import { INPUT_ANTRAGSDATUM, OUTPUT_ANTRAGSDATUM } from "../../utilities/variables/modulfRuntimeAnnotations";
import { findAnnotations } from "../../utilities/modelHelpers";

/**
 * A saga that prepares data for a server side date validations that depends on antransdatum.
 * When Antrasdatum is changed, it reads antragsdatum value and copies it to every module,
 * that has a special annotation.
 */
export function* datumValidationSaga(): SagaGenerator<void> {
	yield* takeLatest(
		(action: AnyAction) =>
			FormEngineActions.event.match(action) && Events.valueChange.match(action.payload.engineEvent),
		function* (action: ReturnType<typeof FormEngineActions.event>) {
			const pathElements = action?.payload?.engineEvent?.payload?.path;

			if (pathElements) {
				const activityId = action.payload.activityId;
				const activity = yield* select(ActivitySelectors.activityById(activityId));
				if (!activity) {
					console.error(`Activity ${activityId} not found.`);
					return;
				}

				const engineState = yield* select(FormEngineSelectors.engineState(activityId));
				if (!engineState) {
					console.error("Couldn't get EngineState");
					return;
				}

				const documentModel = ModelSelectors.documentModel()(engineState);
				const antragAnnotations = findAnnotations(documentModel, INPUT_ANTRAGSDATUM);
				const isAntragsdatumUpdate = Array.from(antragAnnotations.entries()).some(
					([paths, _]) => paths[paths.length - 1]?.elementName == pathElements.at(-1)?.elementName
				);

				if (isAntragsdatumUpdate) {
					const dateAnnotations = findAnnotations(documentModel, OUTPUT_ANTRAGSDATUM);
					if (dateAnnotations.size > 0) {
						const antragsdatum = action.payload.engineEvent.payload.value;
						const documentService = new DocumentServiceFactory().getDocumentService();
						const data = yield* select(ActivitySelectors.data(activityId)) as any;
						const updatedDocument = Array.from(dateAnnotations.entries())
							.map(([paths, _]) => paths)
							.reduce((document, annotationPaths) => {
								annotationPaths.shift(); //remove RootGroup element
								const target = annotationPaths.pop()!;
								const parentEntityInstancePath = createZeroIndexedEntityInstancePath(
									createStringValidationPath(annotationPaths)
								);
								const ensuredDocument = ensureParent(
									document,
									[...annotationPaths],
									parentEntityInstancePath,
									documentService,
									documentModel
								);
								if (ensuredDocument) {
									const path = createPath(
										documentService,
										ensuredDocument,
										parentEntityInstancePath,
										target
									);
									return documentService.updateEntityInstance(
										ensuredDocument,
										path,
										antragsdatum,
										documentModel
									);
								}
								return document;
							}, data?.document);
						yield* put(
							ActivityActions.setData({
								activityId,
								data: { document: updatedDocument }
							})
						);
					}
				}
			}
		}
	);
}

export const createStringValidationPath = (paths: ModelPath) =>
	paths.map(pathObj => pathObj.elementName).reduce((pathAcc, pathElement) => pathAcc + "/" + pathElement, "");

export function createPath(
	documentService: DocumentService,
	document: GroupInstance,
	parentEntityInstancePath: EntityInstancePath,
	target: { readonly elementName: string }
) {
	const path = documentService.getEntityPathsInDocument(document, parentEntityInstancePath)[0];
	path?.push({ elementName: target.elementName, index: 1 });
	return path;
}

export function ensureParent(
	document: Document,
	paths: { readonly elementName: string }[],
	parentEntityInstancePath: { readonly elementName: string; readonly index: number }[],
	documentService: DocumentService,
	documentModel: DocumentModel
) {
	const parentPath = documentService.getEntityPathsInDocument(document, parentEntityInstancePath)[0];
	if (parentPath) {
		return document;
	}
	const parent = paths.pop()!;
	const entityInstancePath = createZeroIndexedEntityInstancePath(createStringValidationPath(paths));
	const path = createPath(documentService, document, entityInstancePath, parent);
	if (!path) {
		return undefined; //Es ist ein Edge Case, wenn Datum in einer Tabelle ist.
	}
	return documentService.updateEntityInstance(document, path, {}, documentModel);
}
